<template>
  <section class="h-full mx-4" id="distributionChannelsManagement">
    <BaseTable
      v-if="distributionChannels"
      :headers="distributionChannelsHeaders"
      :list="distributionChannels"
      clickable
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      @table-item-clicked="onItemClick"
      table-id="document-types"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DistributionChannel)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import {
  getAllDistributionChannels,
  removeDistributionChannelsByIds,
} from '@/services/api/documents';
import { filterActive, filterData, sortData } from '@/utils/table';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type DistributionChannel } from '@/types/DistributionChannel';
import type { TableFilterData } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const { reloadLookups } = useLookupsStore();

const toastStore = useToastStore();
const router = useRouter();
const distributionChannels = ref<DistributionChannel[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const distributionChannelsHeaders = [
  { value: 'company.name', text: $t('distribution-channel.company'), type: 'text', active: true },
  {
    value: 'mwAccountGroup.accountGroup',
    text: $t('material.mwAccountGroup'),
    type: 'text',
    active: true,
  },
  { value: 'isDefault', text: $t('distribution-channel.isDefault'), type: 'check', active: true },
  { value: 'exvan', text: $t('distribution-channel.exvan'), type: 'check', active: true },
  { value: 'orders', text: $t('distribution-channel.orders'), type: 'check', active: true },
  { value: 'logistics', text: $t('distribution-channel.logistics'), type: 'check', active: true },
  {
    value: 'uniqueTradersPerRoute',
    text: $t('distribution-channel.uniqueTradersPerRoute'),
    type: 'check',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData() {
  const res = await getAllDistributionChannels();
  if (res) {
    distributionChannels.value = res;
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DistributionChannel>();
function onDeleteRow(item: DistributionChannel) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDistributionChannelsByIds([idToDelete]);
  if (!res && distributionChannels.value)
    distributionChannels.value = distributionChannels.value.filter(
      (type) => type.id !== idToDelete
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['distributionChannels']);
}

async function onDeleteMultiselect(items: DistributionChannel[]) {
  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeDistributionChannelsByIds(idsToDelete);
  if (!res && distributionChannels.value)
    distributionChannels.value = distributionChannels.value.filter(
      (type) => !idsToDelete.includes(type.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['distributionChannels']);
}

async function refreshData(data: TableFilterData) {
  await loadData();
  if (data.query && distributionChannels.value) {
    distributionChannels.value = filterData(distributionChannels.value, data.query, [
      'company.name',
      'company.code',
      'division.name',
      'mwAccountGroup.accountGroup',
    ]);
  }

  if (data.column !== undefined && data.order && distributionChannels.value) {
    distributionChannels.value = sortData(
      distributionChannels.value,
      data.column,
      data.order,
      distributionChannelsHeaders
    );
  }

  if (data.isActive !== undefined && distributionChannels.value)
    distributionChannels.value = filterActive(distributionChannels.value, data.isActive);
}

function onAddNew() {
  router.push({ name: 'distribution-channels-details', params: { id: uuidv4() } });
}

function onItemClick(item: DistributionChannel) {
  if (!item.deleted)
    router.push({
      name: 'distribution-channels-details',
      params: { id: item.id },
    });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
